import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import { Box, InputBase, MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material'
import { memo, useCallback, useMemo, useState } from 'react'
import { TransactionType } from '../../../../proto/generated/order'
import { QuoteType } from '../../../../proto/generated/quick-trade'
import { OrderBook } from '../../../../proto/generated/security'
import {
    clampQuoteValue,
    formatLimitPriceDisplay,
    formatPriceOffsetLabel,
    LEVEL_QUOTE_MAX,
    LEVEL_QUOTE_MIN,
    quoteTypeLabel,
} from '../../../../util/quickTradePriceOffset'
import { computeQuickTradeEntryPriceDisplay, QuickTradeQuoteScalars } from '../../../../util/quickTradeQuote'
import { fieldArrowSx, fieldShellSx, typeLabelTypographySx, typeSelectSx, typeSelectWrapSx } from './quickTradeFieldChrome'

export type QuickTradePriceOffsetFieldProps = {
    quoteType: QuoteType
    quoteValue: number | '' | undefined
    disabled?: boolean
    onQuoteTypeChange: (type: QuoteType) => void
    onQuoteValueChange: (value: number) => void
    transactionType: TransactionType
    quote: QuickTradeQuoteScalars
    orderBook?: OrderBook
    priceMultiplier: number
    tickSize: number
    menuZIndex?: number
    labelAdornment?: React.ReactNode
}

const QuickTradePriceOffsetField: React.FC<QuickTradePriceOffsetFieldProps> = ({
    quoteType,
    quoteValue,
    disabled = false,
    onQuoteTypeChange,
    onQuoteValueChange,
    transactionType,
    quote,
    orderBook,
    priceMultiplier,
    tickSize,
    menuZIndex = 13000,
    labelAdornment,
}) => {
    const [isEditing, setIsEditing] = useState(false)
    const [draftValue, setDraftValue] = useState('')

    const numericValue = useMemo(() => {
        if (quoteValue === '' || quoteValue == null) return undefined
        const n = Number(quoteValue)
        return Number.isFinite(n) ? n : undefined
    }, [quoteValue])

    const effectiveQuoteValue = useMemo(() => {
        if (!isEditing) return numericValue
        if (draftValue === '' || draftValue === '-') return numericValue
        const n = Number(draftValue)
        if (!Number.isFinite(n)) return numericValue
        return clampQuoteValue(quoteType, n)
    }, [isEditing, draftValue, quoteType, numericValue])

    const limitPriceDisplay = useMemo(
        () =>
            disabled
                ? undefined
                : computeQuickTradeEntryPriceDisplay({
                      quote,
                      orderBook,
                      transactionType,
                      quoteType,
                      quoteValue: effectiveQuoteValue,
                      tickSize,
                      priceMultiplier,
                  }),
        [disabled, quote, orderBook, transactionType, quoteType, effectiveQuoteValue, tickSize, priceMultiplier]
    )

    const blurLabel = useMemo(
        () => formatPriceOffsetLabel(transactionType, quoteType, effectiveQuoteValue ?? 0),
        [transactionType, quoteType, effectiveQuoteValue]
    )

    const lhsDisplay = isEditing ? draftValue : blurLabel

    const handleFocus = useCallback(() => {
        if (disabled) return
        setDraftValue(numericValue != null ? String(numericValue) : '')
        setIsEditing(true)
    }, [disabled, numericValue])

    const handleBlur = useCallback(() => {
        setIsEditing(false)
        const clamped = clampQuoteValue(quoteType, draftValue)
        onQuoteValueChange(clamped)
    }, [quoteType, draftValue, onQuoteValueChange])

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            let next = e.target.value
            if (quoteType === QuoteType.LEVEL && next !== '' && next !== '-') {
                const n = Number(next)
                if (Number.isFinite(n)) next = String(clampQuoteValue(QuoteType.LEVEL, n))
            }
            setDraftValue(next)
        },
        [quoteType]
    )

    const disabledShellSx = disabled
        ? { opacity: 0.5, pointerEvents: 'none' as const, '&:focus-within': { borderColor: 'divider' } }
        : {}

    return (
        <Stack spacing={0} sx={{ width: '100%' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                <Box sx={typeSelectWrapSx}>
                    <Select<QuoteType>
                        value={quoteType}
                        variant="standard"
                        disableUnderline
                        disabled={disabled}
                        onChange={(e: SelectChangeEvent<QuoteType>) =>
                            onQuoteTypeChange(e.target.value as QuoteType)
                        }
                        MenuProps={{ sx: { zIndex: menuZIndex } }}
                        sx={typeSelectSx}
                        renderValue={() => (
                            <Box
                                component="span"
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: 'text.secondary' }}
                            >
                                <Typography component="span" variant="caption" sx={typeLabelTypographySx}>
                                    Price Offset
                                </Typography>
                                <KeyboardArrowDown sx={{ fontSize: 16, color: 'text.secondary' }} />
                            </Box>
                        )}
                    >
                        <MenuItem value={QuoteType.BIPS} dense>
                            {quoteTypeLabel(QuoteType.BIPS)}
                        </MenuItem>
                        <MenuItem value={QuoteType.TICKS} dense>
                            {quoteTypeLabel(QuoteType.TICKS)}
                        </MenuItem>
                        <MenuItem value={QuoteType.LEVEL} dense>
                            {quoteTypeLabel(QuoteType.LEVEL)}
                        </MenuItem>
                    </Select>
                </Box>
                {labelAdornment}
            </Box>
            <Box sx={{ ...fieldShellSx, ...disabledShellSx }}>
                <InputBase
                    type={isEditing ? 'number' : 'text'}
                    value={lhsDisplay}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    readOnly={!isEditing}
                    inputProps={
                        isEditing
                            ? quoteType === QuoteType.LEVEL
                                ? { step: 1, min: LEVEL_QUOTE_MIN, max: LEVEL_QUOTE_MAX }
                                : { step: 1 }
                            : { readOnly: true }
                    }
                    sx={{
                        minWidth: 0,
                        fontSize: '0.875rem',
                        fontVariantNumeric: 'tabular-nums',
                        cursor: disabled ? 'default' : isEditing ? 'text' : 'pointer',
                        '& input[type=number]': { MozAppearance: 'textfield' },
                        '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                        },
                    }}
                />
                <Typography component="span" sx={fieldArrowSx}>
                    →
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', minWidth: 0 }}
                >
                    {disabled ? '—' : formatLimitPriceDisplay(limitPriceDisplay)}
                </Typography>
            </Box>
        </Stack>
    )
}

export default memo(QuickTradePriceOffsetField)
